import { useState, useEffect, useRef, useCallback } from "react";
import {
  Headphones, X, Volume2, VolumeX,
  CloudRain, TreePine, Waves, Coffee,
  Flame, Moon, CloudLightning, Wind
} from "lucide-react";
import { cn } from "@/lib/utils";

const SOUNDSCAPES = [
  { key: "rain", name: "Rain", icon: CloudRain, color: "#4a9eda", desc: "Gentle rainfall" },
  { key: "forest", name: "Forest", icon: TreePine, color: "#4caf50", desc: "Birds & rustling leaves" },
  { key: "ocean", name: "Ocean Waves", icon: Waves, color: "#26c6da", desc: "Rolling shoreline" },
  { key: "cafe", name: "Cafe", icon: Coffee, color: "#8d6e63", desc: "Soft chatter & cups" },
  { key: "fireplace", name: "Fireplace", icon: Flame, color: "#ff7043", desc: "Warm crackling" },
  { key: "crickets", name: "Night Crickets", icon: Moon, color: "#7e57c2", desc: "Peaceful nighttime" },
  { key: "thunder", name: "Thunderstorm", icon: CloudLightning, color: "#546e7a", desc: "Distant rumbles" },
  { key: "wind", name: "Wind", icon: Wind, color: "#78909c", desc: "Soft breezes" },
];

// ---- Procedural Audio Engine ----
class AmbientEngine {
  constructor() {
    this.ctx = null;
    this.masterGain = null;
    this.nodes = [];
    this.active = null;
    this.timers = [];
  }

  init() {
    if (this.ctx) return;
    this.ctx = new (window.AudioContext || window.webkitAudioContext)();
    this.masterGain = this.ctx.createGain();
    this.masterGain.connect(this.ctx.destination);
    this.masterGain.gain.value = 0.5;
  }

  setVolume(v) {
    if (this.masterGain) {
      this.masterGain.gain.setTargetAtTime(v, this.ctx.currentTime, 0.1);
    }
  }

  stop() {
    this.timers.forEach(t => clearInterval(t));
    this.timers = [];
    this.nodes.forEach(n => {
      try { n.stop?.(); } catch {}
      try { n.disconnect?.(); } catch {}
    });
    this.nodes = [];
    this.active = null;
  }

  createNoise(type = "white") {
    const bufferSize = this.ctx.sampleRate * 2;
    const buffer = this.ctx.createBuffer(1, bufferSize, this.ctx.sampleRate);
    const data = buffer.getChannelData(0);
    let b0 = 0, b1 = 0, b2 = 0, b3 = 0, b4 = 0, b5 = 0, b6 = 0;
    for (let i = 0; i < bufferSize; i++) {
      const white = Math.random() * 2 - 1;
      if (type === "white") {
        data[i] = white * 0.5;
      } else if (type === "pink") {
        b0 = 0.99886 * b0 + white * 0.0555179;
        b1 = 0.99332 * b1 + white * 0.0750759;
        b2 = 0.96900 * b2 + white * 0.1538520;
        b3 = 0.86650 * b3 + white * 0.3104856;
        b4 = 0.55000 * b4 + white * 0.5329522;
        b5 = -0.7616 * b5 - white * 0.0168980;
        data[i] = (b0 + b1 + b2 + b3 + b4 + b5 + b6 + white * 0.5362) * 0.11;
        b6 = white * 0.115926;
      } else { // brown
        data[i] = (b0 = (b0 + (0.02 * white)) / 1.02) * 3.5;
      }
    }
    const src = this.ctx.createBufferSource();
    src.buffer = buffer;
    src.loop = true;
    this.nodes.push(src);
    return src;
  }

  createFilter(type, freq, q = 1) {
    const filter = this.ctx.createBiquadFilter();
    filter.type = type;
    filter.frequency.value = freq;
    filter.Q.value = q;
    this.nodes.push(filter);
    return filter;
  }

  createGain(value = 1) {
    const gain = this.ctx.createGain();
    gain.gain.value = value;
    this.nodes.push(gain);
    return gain;
  }

  play(key) {
    this.init();
    this.stop();
    this.active = key;

    const generators = {
      rain: () => this.genRain(),
      forest: () => this.genForest(),
      ocean: () => this.genOcean(),
      cafe: () => this.genCafe(),
      fireplace: () => this.genFireplace(),
      crickets: () => this.genCrickets(),
      thunder: () => this.genThunder(),
      wind: () => this.genWind(),
    };

    if (generators[key]) generators[key]();
  }

  genRain() {
    // Base: filtered white noise
    const noise = this.createNoise("white");
    const bp = this.createFilter("bandpass", 3000, 0.5);
    const lp = this.createFilter("lowpass", 6000);
    const gain = this.createGain(0.35);
    noise.connect(bp).connect(lp).connect(gain).connect(this.masterGain);
    noise.start();

    // Drip layer: higher frequency intermittent noise
    const drip = this.createNoise("white");
    const dripBp = this.createFilter("bandpass", 5000, 2);
    const dripGain = this.createGain(0.08);
    drip.connect(dripBp).connect(dripGain).connect(this.masterGain);
    drip.start();

    // Modulate drip volume
    const lfo = this.ctx.createOscillator();
    lfo.type = "sine";
    lfo.frequency.value = 0.3;
    const lfoGain = this.ctx.createGain();
    lfoGain.gain.value = 0.06;
    lfo.connect(lfoGain).connect(dripGain.gain);
    lfo.start();
    this.nodes.push(lfo, lfoGain);
  }

  genForest() {
    // Base: pink noise for rustling
    const noise = this.createNoise("pink");
    const lp = this.createFilter("lowpass", 2500);
    const gain = this.createGain(0.2);
    noise.connect(lp).connect(gain).connect(this.masterGain);
    noise.start();

    // Bird chirps: periodic oscillator bursts
    const birdTimer = setInterval(() => {
      if (!this.ctx || this.active !== "forest") return;
      const osc = this.ctx.createOscillator();
      const g = this.ctx.createGain();
      osc.type = "sine";
      osc.frequency.value = 2000 + Math.random() * 2000;
      g.gain.setValueAtTime(0, this.ctx.currentTime);
      g.gain.linearRampToValueAtTime(0.04, this.ctx.currentTime + 0.05);
      g.gain.linearRampToValueAtTime(0, this.ctx.currentTime + 0.15);
      osc.connect(g).connect(this.masterGain);
      osc.start();
      osc.stop(this.ctx.currentTime + 0.2);
    }, 1500 + Math.random() * 3000);
    this.timers.push(birdTimer);
  }

  genOcean() {
    // Brown noise base
    const noise = this.createNoise("brown");
    const lp = this.createFilter("lowpass", 800);
    const gain = this.createGain(0.4);
    noise.connect(lp).connect(gain).connect(this.masterGain);
    noise.start();

    // Wave modulation via LFO on gain
    const lfo = this.ctx.createOscillator();
    lfo.type = "sine";
    lfo.frequency.value = 0.08;
    const lfoGain = this.ctx.createGain();
    lfoGain.gain.value = 0.25;
    lfo.connect(lfoGain).connect(gain.gain);
    lfo.start();
    this.nodes.push(lfo, lfoGain);

    // Foam layer
    const foam = this.createNoise("white");
    const foamBp = this.createFilter("bandpass", 4000, 0.3);
    const foamGain = this.createGain(0.05);
    foam.connect(foamBp).connect(foamGain).connect(this.masterGain);
    foam.start();

    const lfo2 = this.ctx.createOscillator();
    lfo2.type = "sine";
    lfo2.frequency.value = 0.06;
    const lfo2Gain = this.ctx.createGain();
    lfo2Gain.gain.value = 0.04;
    lfo2.connect(lfo2Gain).connect(foamGain.gain);
    lfo2.start();
    this.nodes.push(lfo2, lfo2Gain);
  }

  genCafe() {
    // Murmur: filtered pink noise
    const noise = this.createNoise("pink");
    const bp = this.createFilter("bandpass", 800, 0.5);
    const gain = this.createGain(0.2);
    noise.connect(bp).connect(gain).connect(this.masterGain);
    noise.start();

    // Ambient layer
    const amb = this.createNoise("brown");
    const ambLp = this.createFilter("lowpass", 400);
    const ambGain = this.createGain(0.1);
    amb.connect(ambLp).connect(ambGain).connect(this.masterGain);
    amb.start();

    // Occasional cup clink
    const clinkTimer = setInterval(() => {
      if (!this.ctx || this.active !== "cafe") return;
      const osc = this.ctx.createOscillator();
      const g = this.ctx.createGain();
      osc.type = "triangle";
      osc.frequency.value = 3000 + Math.random() * 2000;
      g.gain.setValueAtTime(0, this.ctx.currentTime);
      g.gain.linearRampToValueAtTime(0.02, this.ctx.currentTime + 0.01);
      g.gain.exponentialRampToValueAtTime(0.001, this.ctx.currentTime + 0.3);
      osc.connect(g).connect(this.masterGain);
      osc.start();
      osc.stop(this.ctx.currentTime + 0.35);
    }, 3000 + Math.random() * 5000);
    this.timers.push(clinkTimer);
  }

  genFireplace() {
    // Base crackle: brown noise
    const noise = this.createNoise("brown");
    const bp = this.createFilter("bandpass", 500, 0.8);
    const gain = this.createGain(0.3);
    noise.connect(bp).connect(gain).connect(this.masterGain);
    noise.start();

    // Crackle pops
    const popTimer = setInterval(() => {
      if (!this.ctx || this.active !== "fireplace") return;
      const buf = this.ctx.createBuffer(1, this.ctx.sampleRate * 0.05, this.ctx.sampleRate);
      const d = buf.getChannelData(0);
      for (let i = 0; i < d.length; i++) d[i] = (Math.random() * 2 - 1) * Math.exp(-i / (d.length * 0.1));
      const src = this.ctx.createBufferSource();
      src.buffer = buf;
      const g = this.ctx.createGain();
      g.gain.value = 0.06 + Math.random() * 0.06;
      const hp = this.ctx.createBiquadFilter();
      hp.type = "highpass";
      hp.frequency.value = 1000;
      src.connect(hp).connect(g).connect(this.masterGain);
      src.start();
    }, 200 + Math.random() * 600);
    this.timers.push(popTimer);
  }

  genCrickets() {
    // Base night ambience
    const noise = this.createNoise("brown");
    const lp = this.createFilter("lowpass", 300);
    const gain = this.createGain(0.12);
    noise.connect(lp).connect(gain).connect(this.masterGain);
    noise.start();

    // Cricket chirps: AM modulated sine waves
    for (let i = 0; i < 3; i++) {
      const freq = 4000 + i * 800 + Math.random() * 500;
      const osc = this.ctx.createOscillator();
      osc.type = "sine";
      osc.frequency.value = freq;
      const amOsc = this.ctx.createOscillator();
      amOsc.type = "square";
      amOsc.frequency.value = 12 + Math.random() * 8;
      const amGain = this.ctx.createGain();
      amGain.gain.value = 0.015;
      const oscGain = this.ctx.createGain();
      oscGain.gain.value = 0;
      amOsc.connect(amGain).connect(oscGain.gain);
      osc.connect(oscGain).connect(this.masterGain);
      osc.start();
      amOsc.start();
      this.nodes.push(osc, amOsc, amGain, oscGain);
    }

    // Periodic chirp bursts
    const chirpTimer = setInterval(() => {
      if (!this.ctx || this.active !== "crickets") return;
      const osc = this.ctx.createOscillator();
      osc.type = "sine";
      osc.frequency.value = 5000 + Math.random() * 1500;
      const g = this.ctx.createGain();
      g.gain.setValueAtTime(0.02, this.ctx.currentTime);
      g.gain.linearRampToValueAtTime(0, this.ctx.currentTime + 0.8);
      osc.connect(g).connect(this.masterGain);
      osc.start();
      osc.stop(this.ctx.currentTime + 0.9);
    }, 2000 + Math.random() * 4000);
    this.timers.push(chirpTimer);
  }

  genThunder() {
    // Rain base (same as rain but heavier)
    const noise = this.createNoise("white");
    const bp = this.createFilter("bandpass", 2500, 0.4);
    const gain = this.createGain(0.4);
    noise.connect(bp).connect(gain).connect(this.masterGain);
    noise.start();

    // Low rumble
    const rumble = this.createNoise("brown");
    const rLp = this.createFilter("lowpass", 200);
    const rGain = this.createGain(0.3);
    rumble.connect(rLp).connect(rGain).connect(this.masterGain);
    rumble.start();

    // Thunder claps
    const thunderTimer = setInterval(() => {
      if (!this.ctx || this.active !== "thunder") return;
      const buf = this.ctx.createBuffer(1, this.ctx.sampleRate * 2, this.ctx.sampleRate);
      const d = buf.getChannelData(0);
      for (let i = 0; i < d.length; i++) {
        const t = i / d.length;
        d[i] = (Math.random() * 2 - 1) * Math.exp(-t * 3) * 0.3;
      }
      const src = this.ctx.createBufferSource();
      src.buffer = buf;
      const lp = this.ctx.createBiquadFilter();
      lp.type = "lowpass";
      lp.frequency.value = 400;
      const g = this.ctx.createGain();
      g.gain.value = 0.2 + Math.random() * 0.2;
      src.connect(lp).connect(g).connect(this.masterGain);
      src.start();
    }, 8000 + Math.random() * 15000);
    this.timers.push(thunderTimer);
  }

  genWind() {
    // Swept filtered noise
    const noise = this.createNoise("white");
    const bp = this.createFilter("bandpass", 400, 0.5);
    const gain = this.createGain(0.25);
    noise.connect(bp).connect(gain).connect(this.masterGain);
    noise.start();

    // Sweep the filter frequency
    const lfo = this.ctx.createOscillator();
    lfo.type = "sine";
    lfo.frequency.value = 0.05;
    const lfoGain = this.ctx.createGain();
    lfoGain.gain.value = 300;
    lfo.connect(lfoGain).connect(bp.frequency);
    lfo.start();
    this.nodes.push(lfo, lfoGain);

    // Whistle layer
    const whistle = this.createNoise("pink");
    const wBp = this.createFilter("bandpass", 1200, 3);
    const wGain = this.createGain(0.04);
    whistle.connect(wBp).connect(wGain).connect(this.masterGain);
    whistle.start();

    const lfo2 = this.ctx.createOscillator();
    lfo2.type = "sine";
    lfo2.frequency.value = 0.12;
    const lfo2Gain = this.ctx.createGain();
    lfo2Gain.gain.value = 500;
    lfo2.connect(lfo2Gain).connect(wBp.frequency);
    lfo2.start();
    this.nodes.push(lfo2, lfo2Gain);
  }
}

// Singleton engine
let engineInstance = null;
const getEngine = () => {
  if (!engineInstance) engineInstance = new AmbientEngine();
  return engineInstance;
};

export const AmbientSounds = () => {
  const [open, setOpen] = useState(false);
  const [playing, setPlaying] = useState(null);
  const [volume, setVolume] = useState(() => {
    const saved = localStorage.getItem("omni_ambient_vol");
    return saved ? parseFloat(saved) : 0.5;
  });
  const dropdownRef = useRef(null);

  // Close dropdown on outside click
  useEffect(() => {
    const handler = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) setOpen(false);
    };
    if (open) document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [open]);

  const toggleSound = useCallback((key) => {
    const engine = getEngine();
    if (playing === key) {
      engine.stop();
      setPlaying(null);
    } else {
      engine.play(key);
      engine.setVolume(volume);
      setPlaying(key);
    }
  }, [playing, volume]);

  const handleVolume = useCallback((e) => {
    const v = parseFloat(e.target.value);
    setVolume(v);
    localStorage.setItem("omni_ambient_vol", String(v));
    const engine = getEngine();
    engine.setVolume(v);
  }, []);

  const stopAll = useCallback(() => {
    getEngine().stop();
    setPlaying(null);
  }, []);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (engineInstance) {
        engineInstance.stop();
      }
    };
  }, []);

  const activeSound = SOUNDSCAPES.find(s => s.key === playing);

  return (
    <div ref={dropdownRef} className="relative">
      <button
        data-testid="ambient-toggle"
        onClick={() => setOpen(!open)}
        className={cn(
          "relative p-2 rounded-full transition-all",
          playing
            ? "text-primary hover:bg-primary/10"
            : "text-muted-foreground hover:bg-muted/50 hover:text-primary"
        )}
        title={playing ? `Now playing: ${activeSound?.name}` : "Ambient sounds"}
      >
        <Headphones className="w-4 h-4" />
        {playing && (
          <span className="absolute -top-0.5 -right-0.5 w-2.5 h-2.5 rounded-full bg-primary animate-pulse" />
        )}
      </button>

      {open && (
        <div
          data-testid="ambient-dropdown"
          className="absolute right-0 top-full mt-2 w-80 bg-card border border-border/50 rounded-2xl shadow-xl z-50 overflow-hidden animate-in fade-in slide-in-from-top-2 duration-200"
        >
          {/* Header */}
          <div className="flex items-center justify-between px-4 pt-4 pb-2">
            <div className="flex items-center gap-2">
              <Headphones className="w-4 h-4 text-primary" />
              <span className="text-sm font-medium">Ambient Sounds</span>
            </div>
            <div className="flex items-center gap-1">
              {playing && (
                <button
                  data-testid="ambient-stop"
                  onClick={stopAll}
                  className="p-1.5 rounded-lg hover:bg-muted/50 text-muted-foreground hover:text-destructive text-xs flex items-center gap-1"
                >
                  <VolumeX className="w-3.5 h-3.5" /> Stop
                </button>
              )}
              <button onClick={() => setOpen(false)} className="p-1.5 rounded-lg hover:bg-muted/50 text-muted-foreground">
                <X className="w-3.5 h-3.5" />
              </button>
            </div>
          </div>

          {/* Volume slider */}
          <div className="px-4 py-2 flex items-center gap-3">
            <VolumeX className="w-3.5 h-3.5 text-muted-foreground flex-shrink-0" />
            <input
              data-testid="ambient-volume"
              type="range"
              min="0"
              max="1"
              step="0.05"
              value={volume}
              onChange={handleVolume}
              className="flex-1 h-1.5 accent-primary cursor-pointer"
            />
            <Volume2 className="w-3.5 h-3.5 text-muted-foreground flex-shrink-0" />
          </div>

          {/* Soundscape Grid */}
          <div className="grid grid-cols-4 gap-2 p-3">
            {SOUNDSCAPES.map(s => {
              const SIcon = s.icon;
              const isActive = playing === s.key;
              return (
                <button
                  key={s.key}
                  data-testid={`ambient-${s.key}`}
                  onClick={() => toggleSound(s.key)}
                  className={cn(
                    "flex flex-col items-center gap-1.5 p-3 rounded-xl border transition-all",
                    isActive
                      ? "border-primary/40 bg-primary/10 shadow-sm"
                      : "border-transparent hover:bg-muted/30"
                  )}
                >
                  <div
                    className={cn(
                      "w-9 h-9 rounded-full flex items-center justify-center transition-all",
                      isActive ? "scale-110" : ""
                    )}
                    style={{ backgroundColor: isActive ? s.color + "20" : "transparent" }}
                  >
                    <SIcon
                      className="w-4 h-4 transition-all"
                      style={{ color: isActive ? s.color : undefined }}
                      strokeWidth={1.5}
                    />
                  </div>
                  <span className={cn(
                    "text-[10px] font-medium leading-tight text-center",
                    isActive ? "text-primary" : "text-muted-foreground"
                  )}>
                    {s.name}
                  </span>
                  {isActive && (
                    <div className="flex gap-0.5">
                      {[0, 1, 2].map(i => (
                        <div
                          key={i}
                          className="w-0.5 rounded-full bg-primary animate-pulse"
                          style={{
                            height: `${6 + Math.random() * 6}px`,
                            animationDelay: `${i * 0.2}s`,
                            animationDuration: `${0.6 + Math.random() * 0.4}s`,
                          }}
                        />
                      ))}
                    </div>
                  )}
                </button>
              );
            })}
          </div>

          {/* Now playing indicator */}
          {activeSound && (
            <div className="px-4 pb-3">
              <div
                className="flex items-center gap-2 px-3 py-2 rounded-xl text-xs"
                style={{ backgroundColor: activeSound.color + "10" }}
              >
                <div className="flex gap-0.5 items-end">
                  {[0, 1, 2, 3].map(i => (
                    <div
                      key={i}
                      className="w-0.5 rounded-full animate-pulse"
                      style={{
                        height: `${4 + Math.random() * 8}px`,
                        backgroundColor: activeSound.color,
                        animationDelay: `${i * 0.15}s`,
                        animationDuration: `${0.5 + Math.random() * 0.5}s`,
                      }}
                    />
                  ))}
                </div>
                <span className="text-muted-foreground">
                  Now playing: <span className="font-medium" style={{ color: activeSound.color }}>{activeSound.name}</span>
                  <span className="opacity-60 ml-1">— {activeSound.desc}</span>
                </span>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};
